import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError, markPoolItemAsOpened, type ApiClient, type IframePoolOrderItem } from '../../api';
import { notifyVaulted } from '../../notifications';
import { releasePreOpenedCard } from './preOpenedCards';

type Params = {
  client: ApiClient;
  orderId: string;
  items: IframePoolOrderItem[];
  /** Leaves the reveal (the screen's own Exit). */
  onExit: () => void;
};

/**
 * Sleek's Exit (partner feedback 2026-10-01): with packs of this order still
 * sealed it asks first — "Close and vault" opens them in the background and
 * leaves (their cards land in the vault), "Continue" stays. With
 * nothing sealed it simply leaves.
 *
 * Opening here is the same `mark_as_opened` the ceremony calls; the card was
 * fixed at purchase, so skipping the ceremony changes nothing about the pull.
 * A card the backend already opened at purchase (open_immediately) is in the
 * vault already: it is only released, no call.
 */
export function useSleekExit({ client, orderId, items, onExit }: Params) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [vaulting, setVaulting] = useState(false);
  const sealed = items.filter((item) => !item.openedAt);

  const requestExit = () => {
    if (sealed.length > 0) setSheetOpen(true);
    else onExit();
  };

  const closeAndVault = async () => {
    if (vaulting) return;
    setVaulting(true);
    const results = await Promise.allSettled(
      sealed.map((item) =>
        releasePreOpenedCard(orderId, String(item.id)) ? Promise.resolve() : markPoolItemAsOpened(client, item.id),
      ),
    );
    // 422 = the ceremony opened it a moment ago; the card is in the vault all the same.
    const vaulted = sealed.filter((_, i) => {
      const result = results[i];
      return result?.status === 'fulfilled' || (result?.reason instanceof ApiError && result.reason.status === 422);
    });
    if (vaulted.length > 0) notifyVaulted(vaulted.length, vaulted[0]?.imageUrl, 'exit');
    const failed = sealed.length - vaulted.length;
    if (failed > 0) {
      const what = failed === 1 ? '1 pack' : `${failed} packs`;
      toast.error(`${what} couldn't be opened. Find ${failed === 1 ? 'it' : 'them'} under Packs in your Vault.`);
    }
    onExit();
  };

  return {
    sheetOpen,
    sealedCount: sealed.length,
    vaulting,
    requestExit,
    closeAndVault,
    continueRipping: () => setSheetOpen(false),
  };
}
